import { STT_LANGUAGES } from './languages'

/** Warn only when the model is at least this sure about the detected language. */
export const MIN_DETECT_CONFIDENCE = 0.8

export interface DetectedLanguage {
  iso: string
  name: string
  confidence: number
}

// VoxLingua107 uses a few legacy ISO codes
const ISO_ALIASES: Record<string, string> = {
  iw: 'he', // Hebrew
  jw: 'jv', // Javanese
  nn: 'no', // Nynorsk → Norwegian
}

function sttBase(code: string): string {
  return code.split('_')[0].toLowerCase()
}

function normalizeIso(iso: string): string {
  const lower = iso.trim().toLowerCase()
  return ISO_ALIASES[lower] ?? lower
}

/**
 * True when an STT language code refers to the same spoken language as a
 * detected ISO code — script variants match their base language, so 'zh'
 * matches both zh-Hans_CN and zh-Hant_TW, 'sr' matches sr-Cyrl_RS.
 */
export function matchesDetected(sttCode: string, detectedIso: string): boolean {
  const base = sttBase(sttCode)
  const iso = normalizeIso(detectedIso)
  return base === iso || base.startsWith(iso + '-')
}

/** First STT language for a detected ISO code, or null when we can't offer a switch. */
export function sttLanguageForIso(detectedIso: string): { name: string; code: string } | null {
  return STT_LANGUAGES.find((lang) => matchesDetected(lang.code, detectedIso)) ?? null
}

export type AutoDecision =
  /** Confident, and a language we transcribe — go ahead with it */
  | { kind: 'use'; language: { name: string; code: string } }
  /** Confident, and a language we do NOT transcribe */
  | { kind: 'unsupported' }
  /** One of ours, but the model is barely sure — which is what a language it
   * has never heard usually looks like */
  | { kind: 'unsure'; language: { name: string; code: string } }
  /** Nothing came back at all */
  | { kind: 'none' }

/**
 * What to do with a detection when the user picked no language.
 *
 * This used to be one line — `sttLanguageForIso(iso)` and go — with the
 * confidence never read at all. So a file in a language the model has never
 * heard came back as its nearest guess at 30 % certainty and was transcribed
 * as that, silently, which from the outside looks exactly like "it picked
 * the closest language it supports".
 */
export function decideAutoLanguage(detected: DetectedLanguage | null): AutoDecision {
  if (!detected) return { kind: 'none' }
  const language = sttLanguageForIso(detected.iso)
  if (!language) return { kind: 'unsupported' }
  if (detected.confidence < MIN_DETECT_CONFIDENCE) return { kind: 'unsure', language }
  return { kind: 'use', language }
}
